/**
 * شاشة «إتاحات التقييم» — `/evaluation/availability`.
 *
 * 🔴 **الكوليكشن كان موجودًا وفاضيًا من يوم ما اتعمل.** شاشة الحجز كانت
 *    مكتوب فيها حرفيًا «إتاحات المقيّمين لسه مش مسجّلة — الموعد بيتكتب
 *    بإيدك»، فموظفة دعم الطلاب بتتفق على ساعة مع ولي الأمر **من غير ما
 *    تعرف مين فاضي**، والتعارض بيبان بعد ما الأسرة تتحجز.
 *
 * الشاشة نُصّين، وكل نص لقسم مختلف:
 *   • **إتاحتي** — المقيّمة بتسجّل أيامها وساعاتها (تكرار أسبوعي).
 *   • **الخريطة** — دعم الطلاب بيشوف مين فاضي إمتى وبيقيّم إيه.
 *
 * ⛔ **ممنوع نخترع إتاحة.** المقيّمة اللي ما سجّلتش بتبان «لسه ما سجّلتش»
 *    بالنص — لا شبكة فاضية تتقرا «مش متاحة»، ولا كاملة تتقرا «متاحة دايمًا».
 */
import { useCallback, useEffect, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useSession } from "@/lib/session";
import { subjectLabel, SUBJECTS } from "@/api/admin";
import {
  WEEK_DAYS,
  fetchMyAvailability,
  fetchSlotMap,
  saveMyAvailability,
  type Availability,
  type Slot,
  type SlotMap,
} from "@/api/availability";

type Pane = "mine" | "map";
type Load = "loading" | "ready" | "offline" | { error: string };

/** تاريخ بصيغة YYYY-MM-DD — الخادم بيتوقعها كده.
 *
 * ⛔ **مش `toISOString`**: دي بتدّي التاريخ بتوقيت عالمي. الموظفة في مصر
 *    الساعة ١ بعد منتصف الليل كانت هتشوف مدى بيبدأ من **إمبارح**، و«أسبوع»
 *    كانت بترجّع ٨ أيام. التاريخ المعروض لازم يبقى تاريخها هي.
 */
function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function Availability() {
  const { me } = useSession();
  // ⚠️ الأقسام الإضافية مش في نوع `CurrentUser`، فبنقراها بحذر بدل ما
  //    نوسّع النوع من هنا — التوسيع محله ملف الجلسة مش شاشة.
  const extra = ((me?.user as unknown as { extra_departments?: string[] })
    ?.extra_departments) ?? [];
  const isEvaluator =
    me?.user.department === "evaluation" || extra.includes("evaluation");

  const [pane, setPane] = useState<Pane>(isEvaluator ? "mine" : "map");

  return (
    <Shell title="إتاحات التقييم">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* ⚠️ التبويب بيتعرض للكل: المقيّمة محتاجة تشوف الخريطة برضه
            عشان تعرف زمايلها فاضيين إمتى قبل ما تغيّر إتاحتها. */}
        {isEvaluator ? (
          <button
            type="button"
            className={`btn sm ${pane === "mine" ? "accent" : "ghost"}`}
            aria-pressed={pane === "mine"}
            onClick={() => setPane("mine")}
          >
            إتاحتي
          </button>
        ) : null}
        <button
          type="button"
          className={`btn sm ${pane === "map" ? "accent" : "ghost"}`}
          aria-pressed={pane === "map"}
          onClick={() => setPane("map")}
        >
          خريطة المقيّمات
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        {pane === "mine" ? <MinePane /> : <MapPane />}
      </div>
    </Shell>
  );
}

/* ══════════════════ إتاحتي ══════════════════ */

function MinePane() {
  const [state, setState] = useState<Load>("loading");
  const [data, setData] = useState<Availability | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const av = await fetchMyAvailability();
      setData(av);
      setSlots(av.slots);
      setState("ready");
    } catch (err) {
      if (err instanceof OfflineError) setState("offline");
      else {
        console.error("[availability] جلب إتاحتي فشل", err);
        setState({ error: errorMessageAr(err) });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function addSlot(day: number) {
    setSlots((prev) => [
      ...prev,
      { day_of_week: day, start_local: "10:00", end_local: "11:00" },
    ]);
  }

  function editSlot(index: number, patch: Partial<Slot>) {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function removeSlot(index: number) {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSave() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await saveMyAvailability(slots);
      setData(res.availability);
      setSlots(res.availability.slots);
      setToast(res.toastAr);
      window.setTimeout(() => setToast(null), 5000);
    } catch (err) {
      console.error("[availability] حفظ الإتاحة فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  if (state === "loading") return <LoadingState />;
  if (state === "offline") return <OfflineState onRetry={() => void load()} />;
  if (typeof state === "object")
    return <ErrorState messageAr={state.error} onRetry={() => void load()} />;

  return (
    <div className="card" style={{ padding: 20, display: "flex",
                                   flexDirection: "column", gap: 14 }}>
      <div className="sec-h">
        <span className="sec-title">إتاحتي الأسبوعية</span>
        <span className="t-meta">{data?.state_ar}</span>
      </div>

      {/* 🔑 التوقيت بيتقال صريح: المقيّمة في مصر والأسرة ممكن تكون في كندا،
          والساعة اللي بتكتبها هنا بتوقيتها هي. */}
      <span className="t-meta">
        الساعات دي بتوقيت الأكاديمية ({data?.tz ?? "Africa/Cairo"}) — والسيستم
        بيحوّلها لتوقيت الأسرة وقت الحجز.
      </span>

      {WEEK_DAYS.map((day) => {
        const rows = slots
          .map((s, i) => ({ s, i }))
          .filter(({ s }) => s.day_of_week === day.value);
        return (
          <div key={day.value} style={{ display: "flex", flexDirection: "column",
                                        gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="t-body" style={{ minWidth: 70 }}>{day.label}</span>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => addSlot(day.value)}
              >
                + فترة
              </button>
              {rows.length === 0 ? (
                // ⛔ **الفرق ده مهم:** اليوم الفاضي عند حد ما سجّلش أصلًا
                //    اتقرا «السيستم مسجّل إني مش متاحة» فقفلت الشاشة وما
                //    عملتش حاجة. «مش متاحة» قرار، و«ما سجّلتيش» غياب قرار.
                <span className="t-meta">
                  {data?.has_availability ? "مش متاحة" : "لسه ما سجّلتيش"}
                </span>
              ) : null}
            </div>
            {rows.map(({ s, i }) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8,
                                    paddingInlineStart: 80 }}>
                <span className="t-meta">من</span>
                {/* dir="ltr" مسموح: وقت لاتيني صافي بلا حرف عربي */}
                <input
                  type="time"
                  dir="ltr"
                  className="field"
                  style={{ width: 120 }}
                  value={s.start_local}
                  disabled={busy}
                  onChange={(e) => editSlot(i, { start_local: e.target.value })}
                />
                <span className="t-meta">لـ</span>
                <input
                  type="time"
                  dir="ltr"
                  className="field"
                  style={{ width: 120 }}
                  value={s.end_local}
                  disabled={busy}
                  onChange={(e) => editSlot(i, { end_local: e.target.value })}
                />
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={busy}
                  onClick={() => removeSlot(i)}
                >
                  شيل
                </button>
              </div>
            ))}
          </div>
        );
      })}

      {error ? (
        <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
      {toast ? (
        <span className="t-meta" role="status" style={{ color: "var(--ok)" }}>
          {toast}
        </span>
      ) : null}

      <div>
        <button
          type="button"
          className={`btn${busy ? " is-busy" : ""}`}
          disabled={busy}
          aria-busy={busy}
          onClick={() => void onSave()}
        >
          {busy ? "بيتحفظ…" : "احفظ الإتاحة"}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ الخريطة ══════════════════ */

function MapPane() {
  const [state, setState] = useState<Load>("loading");
  const [map, setMap] = useState<SlotMap | null>(null);
  const [subject, setSubject] = useState("");
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await fetchSlotMap({
        from: isoDay(0),
        to: isoDay(days),
        ...(subject ? { subject } : {}),
      });
      setMap(res);
      setState("ready");
    } catch (err) {
      if (err instanceof OfflineError) setState("offline");
      else {
        console.error("[availability] جلب الخريطة فشل", err);
        setState({ error: errorMessageAr(err) });
      }
    }
  }, [subject, days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap",
                    alignItems: "center" }}>
        <span className="t-meta">المادة</span>
        <select
          className="field"
          aria-label="فلترة بالمادة"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        >
          <option value="">كل المواد</option>
          {SUBJECTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <span className="t-meta">المدى</span>
        <select
          className="field"
          aria-label="مدى الأيام"
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value="7">أسبوع</option>
          <option value="14">أسبوعين</option>
        </select>
      </div>

      {state === "loading" ? <LoadingState /> : null}
      {state === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
      {typeof state === "object" ? (
        <ErrorState messageAr={state.error} onRetry={() => void load()} />
      ) : null}

      {state === "ready" && map ? (
        <>
          {/* ⛔ الرقم ده بيتقال صريح: خريطة ناقصة أسوأ من خريطة مفيش، لأنها
              بتتقرا «دول كل المتاحين» وهم مش كده. */}
          {map.withoutAvailability > 0 ? (
            <div className="card" style={{ padding: 12 }}>
              <span className="t-desc">
                {/* ⚠️ الرقم بيتحسب **بعد الفلترة** — فالنص لازم يقول كده،
                    وإلا بيتقرا «السيستم شبه مكتمل» وهو مش كده. */}
                فيه {map.withoutAvailability} من المقيّمات الظاهرين بالفلتر ده لسه
                ما سجّلوش إتاحتهم — مواعيدهم مش ظاهرة هنا، والحجز معاهم بيتكتب
                بالإيد.
              </span>
            </div>
          ) : null}

          {map.rows.length === 0 ? (
            // ⚠️ **تلات أسباب مختلفة كانت بتدّي نفس الرسالة (اتكشف بالتجربة
            //    على الإنتاج):** مفيش مقيّمين في السيستم أصلًا · فيه مقيّمين
            //    بس الفلتر استبعدهم · فيه مقيّمين مطابقين بس مفيش وقت فاضي.
            //    «جرّب مدى أطول» بتنفع في التالتة بس، وفي الأولانيين بتخلّي
            //    الموظفة تلف في الفاضي.
            <EmptyState
              title={
                map.evaluatorsTotal === 0
                  ? "مفيش مقيّمين في السيستم لسه"
                  : subject
                    ? "مفيش مقيّمة متخصصة في المادة دي ومتاحة في المدى ده"
                    : "مفيش مقيّمة متاحة في المدى ده"
              }
              hint={
                map.evaluatorsTotal === 0
                  ? "من شاشة «المستخدمون والصلاحيات» حط الحسابات على قسم «التقييم والترشيح» وحدّد تخصصاتها"
                  : subject
                    ? "شيل فلتر المادة، أو حدّد تخصصات المقيّمات من شاشة المستخدمين"
                    : "جرّب مدى أطول"
              }
            />
          ) : (
            map.rows.map((row) => (
              <div key={row.evaluator.id} className="card"
                   style={{ padding: 16, display: "flex",
                            flexDirection: "column", gap: 10 }}>
                <div className="sec-h">
                  <span className="sec-title">{row.evaluator.name_ar}</span>
                  <span className="t-meta">
                    {row.evaluator.subjects.length
                      ? row.evaluator.subjects.map(subjectLabel).join(" · ")
                      : "بلا تخصص مسجّل"}
                  </span>
                </div>

                {row.free_slots.length === 0 ? (
                  // 🔑 «إتاحة مسجّلة» فوق قايمة فاضية بتتقرا عطل. السبب
                  //    الحقيقي إما كله محجوز أو الأيام برّه المدى — والرقم
                  //    موجود جاهز في الرد وكان مترمي.
                  <span className="t-meta">
                    {row.availability.has_availability
                      ? row.booked_count > 0
                        ? `كل مواعيدها في المدى ده محجوزة (${row.booked_count} حجز)`
                        : "سجّلت إتاحة بس مفيش منها حاجة في المدى ده"
                      : row.availability.state_ar}
                  </span>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.free_slots.slice(0, 40).map((s, i) => (
                      <span key={i} className="chip">
                        {s.day_label_ar} {s.date_local} · {s.start_local}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </>
      ) : null}
    </div>
  );
}
